<script setup>
/**
 * GlPopover — accessible popover component.
 *
 * Forward-compatible with @gitlab/ui's GlPopover:
 *   - `target`          (String|HTMLElement) — element ID or ref to anchor the popover to
 *   - `triggers`        (String)            — space-separated trigger types: "hover", "focus", "click", "manual"
 *   - `title`           (String)            — popover header text
 *   - `content`         (String)            — popover body text (alternative to default slot)
 *   - `placement`       (String)            — position: "top", "right", "bottom", "left"
 *   - `showCloseButton` (Boolean)           — show close button in header
 *   - `cssClasses`      (Array|String|Object) — additional CSS classes
 *   - `disabled`        (Boolean)           — disable the popover
 *   - `delay`           (Number|Object)     — show/hide delay in ms
 *   - `boundary`        (String)            — boundary constraint (unused, kept for API compat)
 *   - `boundaryPadding` (Number|String)     — padding from boundary (unused, kept for API compat)
 *
 * Slots: `title` (header), `default` (body)
 * Events: `show`, `shown`, `hide`, `hidden`, `close-button-clicked`
 *
 * A future swap to the native @gitlab/ui GlPopover requires only changing the import path.
 */
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { iconHref } from "~/utils/icons.js";

const ARROW_SIZE = 8;

const {
  boundary,
  boundaryPadding,
  content,
  cssClasses,
  delay,
  disabled,
  placement,
  showCloseButton,
  target,
  title,
  triggers
} = defineProps({
  boundary: { default: "scrollParent", type: String },
  boundaryPadding: { default: 5, type: [Number, String] },
  content: { default: "", type: String },
  cssClasses: { default: "", type: [Array, String, Object] },
  delay: { default: () => ({ hide: 100, show: 200 }), type: [Number, Object] },
  disabled: { default: false, type: Boolean },
  placement: { default: "top", type: String },
  showCloseButton: { default: false, type: Boolean },
  target: { default: null, type: [String, Object] },
  title: { default: "", type: String },
  triggers: { default: "hover focus", type: String }
});

const emit = defineEmits(["show", "shown", "hide", "hidden", "close-button-clicked"]);

const isVisible = ref(false);
const popoverRef = ref(null);
const position = ref({ left: 0, top: 0 });

let showTimeout = null;
let hideTimeout = null;
let targetEl = null;

const showDelay = computed(() => (typeof delay === "number" ? delay : (delay?.show ?? 200)));
const hideDelay = computed(() => (typeof delay === "number" ? delay : (delay?.hide ?? 100)));

const triggerSet = computed(() => new Set(triggers.split(/\s+/).filter(Boolean)));

const hasTitle = computed(() => Boolean(title));

function resolveTarget() {
  if (!target) return null;
  if (typeof target === "string") {
    return document.getElementById(target);
  }
  return target.$el ?? target;
}

function updatePosition() {
  if (!targetEl || !popoverRef.value) return;

  const targetRect = targetEl.getBoundingClientRect();
  const popoverRect = popoverRef.value.getBoundingClientRect();

  let top = 0;
  let left = 0;

  switch (placement) {
    case "bottom":
      top = targetRect.bottom + window.scrollY + ARROW_SIZE;
      left = targetRect.left + window.scrollX + targetRect.width / 2 - popoverRect.width / 2;
      break;
    case "left":
      top = targetRect.top + window.scrollY + targetRect.height / 2 - popoverRect.height / 2;
      left = targetRect.left + window.scrollX - popoverRect.width - ARROW_SIZE;
      break;
    case "right":
      top = targetRect.top + window.scrollY + targetRect.height / 2 - popoverRect.height / 2;
      left = targetRect.right + window.scrollX + ARROW_SIZE;
      break;
    default:
      top = targetRect.top + window.scrollY - popoverRect.height - ARROW_SIZE;
      left = targetRect.left + window.scrollX + targetRect.width / 2 - popoverRect.width / 2;
      break;
  }

  position.value = { left, top };
}

function scheduleShow() {
  if (disabled) return;
  clearTimeout(hideTimeout);
  showTimeout = setTimeout(async () => {
    emit("show");
    isVisible.value = true;
    await nextTick();
    updatePosition();
    emit("shown");
  }, showDelay.value);
}

function scheduleHide() {
  clearTimeout(showTimeout);
  hideTimeout = setTimeout(() => {
    emit("hide");
    isVisible.value = false;
    emit("hidden");
  }, hideDelay.value);
}

function onCloseButtonClick() {
  emit("close-button-clicked");
  clearTimeout(showTimeout);
  isVisible.value = false;
  emit("hidden");
}

function onPopoverMouseenter() {
  if (triggerSet.value.has("hover")) {
    clearTimeout(hideTimeout);
  }
}

function onPopoverMouseleave() {
  if (triggerSet.value.has("hover")) {
    scheduleHide();
  }
}

function bindTargetListeners() {
  if (!targetEl) return;

  if (triggerSet.value.has("hover")) {
    targetEl.addEventListener("mouseenter", scheduleShow);
    targetEl.addEventListener("mouseleave", scheduleHide);
  }
  if (triggerSet.value.has("focus")) {
    targetEl.addEventListener("focusin", scheduleShow);
    targetEl.addEventListener("focusout", scheduleHide);
  }
  if (triggerSet.value.has("click")) {
    targetEl.addEventListener("click", toggleOnClick);
  }
}

function unbindTargetListeners() {
  if (!targetEl) return;
  targetEl.removeEventListener("mouseenter", scheduleShow);
  targetEl.removeEventListener("mouseleave", scheduleHide);
  targetEl.removeEventListener("focusin", scheduleShow);
  targetEl.removeEventListener("focusout", scheduleHide);
  targetEl.removeEventListener("click", toggleOnClick);
}

function toggleOnClick() {
  if (isVisible.value) {
    scheduleHide();
  } else {
    scheduleShow();
  }
}

watch(
  () => target,
  () => {
    unbindTargetListeners();
    targetEl = resolveTarget();
    bindTargetListeners();
  }
);

watch(
  () => disabled,
  (disabled) => {
    if (disabled && isVisible.value) {
      isVisible.value = false;
      emit("hidden");
    }
  }
);

onMounted(() => {
  targetEl = resolveTarget();
  bindTargetListeners();
});

onBeforeUnmount(() => {
  clearTimeout(showTimeout);
  clearTimeout(hideTimeout);
  unbindTargetListeners();
});

defineExpose({
  hide: scheduleHide,
  show: scheduleShow
});
</script>

<template>
  <Teleport to="body">
    <Transition name="gl-popover">
      <div
        v-if="isVisible"
        ref="popoverRef"
        role="tooltip"
        :class="['gl-popover', cssClasses]"
        :style="{
          position: 'absolute',
          top: `${position.top}px`,
          left: `${position.left}px`,
          zIndex: 9999
        }"
        @mouseenter="onPopoverMouseenter"
        @mouseleave="onPopoverMouseleave"
      >
        <!-- Arrow -->
        <div :class="['gl-popover-arrow', `gl-popover-arrow-${placement}`]" />

        <!-- Header -->
        <div v-if="hasTitle || showCloseButton" class="gl-popover-header">
          <slot name="title">
            <span v-if="hasTitle" class="gl-popover-title">{{ title }}</span>
          </slot>
          <button
            v-if="showCloseButton"
            type="button"
            aria-label="Close"
            class="gl-popover-close"
            @click="onCloseButtonClick"
          >
            <svg width="12" height="12" aria-hidden="true">
              <use :href="iconHref('close')" />
            </svg>
          </button>
        </div>

        <!-- Body -->
        <div class="gl-popover-body">
          <slot>{{ content }}</slot>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.gl-popover {
  max-width: 280px;
  background: var(--gl-background-color-default);
  border-radius: 0.25rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  font-size: 0.75rem;
  line-height: 1.25rem;
  color: var(--gl-text-color-default);
  pointer-events: auto;
}

.gl-popover-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid var(--gl-border-color-default);
}

.gl-popover-title {
  font-weight: 600;
  color: var(--gl-text-color-heading);
}

.gl-popover-close {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.125rem;
  border: none;
  background: transparent;
  color: var(--gl-text-color-subtle);
  cursor: pointer;
  border-radius: 0.25rem;
}

.gl-popover-close:hover {
  color: var(--gl-text-color-default);
}

.gl-popover-body {
  padding: 0.5rem 0.75rem;
}

/* Arrow base */
.gl-popover-arrow {
  position: absolute;
  width: 8px;
  height: 8px;
  background: var(--gl-background-color-default);
  transform: rotate(45deg);
}

.gl-popover-arrow-top {
  bottom: -4px;
  left: calc(50% - 4px);
  box-shadow: 2px 2px 3px rgba(0, 0, 0, 0.06);
}

.gl-popover-arrow-bottom {
  top: -4px;
  left: calc(50% - 4px);
  box-shadow: -2px -2px 3px rgba(0, 0, 0, 0.06);
}

.gl-popover-arrow-left {
  right: -4px;
  top: calc(50% - 4px);
  box-shadow: 2px -2px 3px rgba(0, 0, 0, 0.06);
}

.gl-popover-arrow-right {
  left: -4px;
  top: calc(50% - 4px);
  box-shadow: -2px 2px 3px rgba(0, 0, 0, 0.06);
}

/* Transition */
.gl-popover-enter-active,
.gl-popover-leave-active {
  transition: opacity 0.15s ease;
}

.gl-popover-enter-from,
.gl-popover-leave-to {
  opacity: 0;
}
</style>
